import {
  OperationAllocationsViewModel,
  AllocationFilterViewModel,
  AllocationFilterModel,
  AllocationModel
} from './allocations.model'
import { apiRepartosPATCH } from '../../api/oData/endpoints'
import {
  getCapacitiesForAllocations,
  patchCapacity
} from '../../api/Pioneer/endpoints/capacities/capacities.api'
import {
  capacidadArrayFromApi,
  operacionArrayFromApi,
  operacionToApiRequestPatch,
  allocationToApiRequestPatch,
  dispatchOperationToApiRequestPost,
  dispatchOperationsToApiRequestPost
} from './allocation.mapper'
import {
  apiFetchOperationAllocations,
  EnumTipoOperacion,
  EnumBloqueoCapacidad,
  apiOperacionesPATCH,
  EnumOperationState,
  EnumDispatchType
} from '../../api'
import { enumKeyToValue } from '../../helpers'

import { ActionIcons } from '../../model/enums'
import { generateIconsFromConfiguration } from '../../helpers/dataTableActionIconHelper'
import i18n from '../../i18n'
import { DataTableActionConfiguration } from '../../model'
import moment from 'moment'
import { getActiveBalanceZone, getActiveCompany, notifyError } from '../../redux/helpers'
import { apiMassiveDispatchesPOST } from '../../api/Pioneer/endpoints/dispatches'
import {
  createErrorServiceResponse,
  createSuccessServiceResponse
} from '../../helpers/serviceResponseHelper'
import { ServiceResponse } from '../../model/serviceResponse'

const operationFilterTypes: Array<string> = [
  enumKeyToValue(EnumTipoOperacion, EnumTipoOperacion.EntradaRtPCPR),
  enumKeyToValue(EnumTipoOperacion, EnumTipoOperacion.EntradaRtPCI),
  enumKeyToValue(EnumTipoOperacion, EnumTipoOperacion.EntradaRtPCY),
  enumKeyToValue(EnumTipoOperacion, EnumTipoOperacion.Inyección),
  enumKeyToValue(EnumTipoOperacion, EnumTipoOperacion.Extracción),
  enumKeyToValue(EnumTipoOperacion, EnumTipoOperacion.Licuefaccion),
  enumKeyToValue(EnumTipoOperacion, EnumTipoOperacion.Regasificacion),
  enumKeyToValue(EnumTipoOperacion, EnumTipoOperacion.SalidaRtPCI)
]

//#region API calls

export const fetchCapacities = async (
  startDate: Date,
  endDate: Date
): Promise<ServiceResponse<any>> => {
  return await getCapacitiesForAllocations(
    startDate,
    endDate,
    getActiveBalanceZone(),
    getActiveCompany()
  )
    .then(response => {
      return createSuccessServiceResponse(groupCapacities(capacidadArrayFromApi(response)))
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const fetchOperationAllocations = async (
  startDate: Date,
  endDate: Date
): Promise<ServiceResponse<any>> => {
  return await apiFetchOperationAllocations(operationFilterTypes, startDate, endDate)
    .then(response => {
      return createSuccessServiceResponse(fillRowsArrayColumns(operacionArrayFromApi(response)))
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const updateOperation = async (operation: OperationAllocationsViewModel) => {
  await apiOperacionesPATCH(operacionToApiRequestPatch(operation)).then(() => {
    operation.allocations
      .filter(x => x.isModified)
      .forEach(async allocation => {
        await apiRepartosPATCH(allocationToApiRequestPatch(allocation))
      })
  })
}

export const updateLockState = async (
  id: number,
  newLockState: EnumBloqueoCapacidad
): Promise<boolean> => {
  return await patchCapacity({
    id: id,
    bloqueo: enumKeyToValue(EnumBloqueoCapacidad, newLockState)
  })
    .then(() => {
      return true
    })
    .catch(() => {
      notifyError(i18n.t('allocations:notifications.error.updateLockState'))
      return false
    })
}

export const sendOperation = async (operation: OperationAllocationsViewModel): Promise<boolean> => {
  let dispatchType: EnumDispatchType = EnumDispatchType.NominacionConRepartos
  if (moment(operation.operationDate).startOf('day') < moment().startOf('day')) {
    dispatchType = EnumDispatchType.Reparto02G
  }

  return await apiMassiveDispatchesPOST(
    dispatchOperationToApiRequestPost(dispatchType, operation, getActiveCompany()?.id!)
  )
    .then(() => {
      return true
    })
    .catch(() => {
      notifyError(i18n.t('allocations:notifications.error.sendOperation'))
      return false
    })
}

export const sendOperations = async (
  operations: OperationAllocationsViewModel[]
): Promise<boolean> => {
  if (canSendMassive(operations)) {
    return (
      (await sendPastOperations(
        operations.filter(x => moment(x.operationDate).startOf('day') < moment().startOf('day'))
      ).then(sent => {
        return sent
      })) &&
      (await sendTodayAndFutureOperations(
        operations.filter(x => moment(x.operationDate).startOf('day') >= moment().startOf('day'))
      ).then(sent => {
        return sent
      }))
    )
  } else {
    notifyError(i18n.t('allocations:notifications.error.notValidToBeSent'))
    return false
  }
}

const sendPastOperations = async (
  operations: OperationAllocationsViewModel[]
): Promise<boolean> => {
  if (operations.length > 0) {
    return await apiMassiveDispatchesPOST(
      dispatchOperationsToApiRequestPost(
        EnumDispatchType.Reparto02G,
        operations,
        getActiveCompany()?.id!
      )
    )
      .then(() => {
        return true
      })
      .catch(() => {
        notifyError(i18n.t('translation:pages.allocations.apiErrors.sendPastOperations'))
        return false
      })
  }
  return true
}

const sendTodayAndFutureOperations = async (
  operations: OperationAllocationsViewModel[]
): Promise<boolean> => {
  if (operations.length > 0) {
    return await apiMassiveDispatchesPOST(
      dispatchOperationsToApiRequestPost(
        EnumDispatchType.NominacionConRepartos,
        operations,
        getActiveCompany()?.id!
      )
    )
      .then(() => {
        return true
      })
      .catch(() => {
        notifyError(i18n.t('allocations:notifications.error.sendTodayAndFutureOperations'))
        return false
      })
  }
  return true
}

//#endregion

export const canSend = (state: EnumOperationState): boolean => {
  return true
}

export const canSendMassive = (operations: OperationAllocationsViewModel[]): boolean => {
  return true
}

export const filterOperationAllocations = (
  operationAllocations: Array<OperationAllocationsViewModel>,
  ids: Array<number> | undefined
): Array<OperationAllocationsViewModel> => {
  if (ids === undefined) {
    return operationAllocations
  }

  let filteredOperationAllocations: Array<OperationAllocationsViewModel> = []

  operationAllocations.forEach(operation => {
    if (
      operation.allocations.filter(
        a => a.productCapacityId !== undefined && ids.includes(a.productCapacityId)
      ).length > 0
    ) {
      filteredOperationAllocations.push(operation)
    }
  })

  return filteredOperationAllocations
}

const fillRowsArrayColumns = (
  items: Array<OperationAllocationsViewModel>
): Array<OperationAllocationsViewModel> => {
  items.forEach(item => {
    fillRowsArrayColumn(item)
  })

  return items
}

export const fillRowsArrayColumn = (
  item: OperationAllocationsViewModel
): OperationAllocationsViewModel => {
  item.allocations.forEach(e => {
    item.allocatedQuantityBreakdownSubRows?.push(e.allocatedQuantityBreakdown)
    item.productCapacityBreakdownSubRows?.push(e.productCapacityBreakdown)
    item.capacitySubRows?.push(e.productQdHiredFormatted)
    item.lastSendBreakdownSubRows?.push(e.lastSendInformationBreakdown)
    item.TSOInformationBreakdownSubRows?.push(e.TSOInformationBreakdown)
  })
  return item
}

export const emptyArrayColumns = (
  item: OperationAllocationsViewModel
): OperationAllocationsViewModel => {
  item.allocations.forEach(() => {
    item.allocatedQuantityBreakdownSubRows = []
    item.productCapacityBreakdownSubRows = []
    item.capacitySubRows = []
    item.lastSendBreakdownSubRows = []
    item.TSOInformationBreakdownSubRows = []
  })
  return item
}

const groupCapacities = (capacities: AllocationFilterModel[]): AllocationFilterViewModel[] => {
  const distinctCapacities: Array<AllocationFilterViewModel> = []

  capacities.forEach(capacity => {
    const existingCapacity = distinctCapacities.find(x => x.description === capacity.description)
    if (existingCapacity !== undefined) {
      existingCapacity.ids.push(capacity.id)
    } else {
      const ids: Array<number> = []
      ids.push(capacity.id)
      distinctCapacities.push({ description: capacity.description, ids: ids })
    }
  })

  return distinctCapacities
}

export const removeEmptyAllocationFilters = (
  filters: AllocationFilterViewModel[],
  operations: OperationAllocationsViewModel[]
) => {
  let nonEmptyFilters: AllocationFilterViewModel[] = []
  filters.forEach(filter => {
    if (filterOperationAllocations(operations, filter.ids).length > 0) {
      nonEmptyFilters.push(filter)
    }
  })
  return nonEmptyFilters
}

export const groupDataTableSubRowActionsColumn = (
  allocations: Array<AllocationModel>,
  handlers: Map<number, Function>
): any => {
  return allocations.map(allocation => {
    if (allocation.actionsConfiguration && allocation.actionsConfiguration.length > 0) {
      return generateIconsFromConfiguration(
        allocation,
        filterAllocationApplicableActionsConfiguration(allocation, allocation.actionsConfiguration),
        handlers
      )
    } else {
      return undefined
    }
  })
}

export const groupDataTableSubRowSecondaryActionsColumn = (
  allocations: Array<AllocationModel>,
  handlers: Map<number, Function>
): any => {
  return allocations.map(allocation => {
    if (
      allocation.secondaryActionsConfiguration &&
      allocation.secondaryActionsConfiguration.length > 0
    ) {
      const applicableConfigurations = filterAllocationApplicableSecondaryActionsConfiguration(
        allocation,
        allocation.secondaryActionsConfiguration
      )

      if (applicableConfigurations.length === 0) {
        applicableConfigurations.push({ icon: ActionIcons.NONE })
      }

      return generateIconsFromConfiguration(allocation, applicableConfigurations, handlers)
    } else {
      return undefined
    }
  })
}

//#region Allocation global actions

export const filterAllocationApplicableActionsConfiguration = (
  item: AllocationModel,
  configurations: Array<DataTableActionConfiguration>
): Array<DataTableActionConfiguration> => {
  let applicableActionsConfiguration: Array<DataTableActionConfiguration> = []
  configurations.forEach(configuration => {
    switch (configuration.icon) {
      case ActionIcons.LOCK: {
        if (item.locked === EnumBloqueoCapacidad.BloqueadoUsuario) {
          applicableActionsConfiguration.push(configuration)
        }
        break
      }
      case ActionIcons.UNLOCK: {
        if (item.locked === EnumBloqueoCapacidad.Libre) {
          applicableActionsConfiguration.push(configuration)
        }
        break
      }
      case ActionIcons.LOCK_DISABLED: {
        if (item.locked === EnumBloqueoCapacidad.BloqueadoBundle) {
          applicableActionsConfiguration.push(configuration)
        }
        break
      }
    }
  })
  return applicableActionsConfiguration
}

export const getAllocationActionsConfiguration = (
  hasPermission: boolean
): Array<DataTableActionConfiguration> => {
  return [
    getAllocationLockAction(hasPermission),
    getAllocationUnlockAction(hasPermission),
    getAllocationLockedAction()
  ]
}

const getAllocationLockedAction = (): DataTableActionConfiguration => {
  return {
    icon: ActionIcons.LOCK_DISABLED,
    noHandlerTooltip: 'Bloqueado'
  }
}

const getAllocationLockAction = (hasPermission: boolean): DataTableActionConfiguration => {
  return {
    icon: ActionIcons.LOCK,
    tooltip: i18n.t('allocations:dataTable.tooltips.lock'),
    noHandlerTooltip: i18n.t('allocations:dataTable.tooltips.lockDisabled'),
    hasPermission: hasPermission
  }
}

const getAllocationUnlockAction = (hasPermission: boolean): DataTableActionConfiguration => {
  return {
    icon: ActionIcons.UNLOCK,
    tooltip: i18n.t('translation:pages.allocations.dataTable.tooltips.unlock'),
    noHandlerTooltip: i18n.t('allocations:dataTable.tooltips.unlockDisabled'),
    handler: undefined,
    hasPermission: hasPermission
  }
}

//#endregion

//#region Allocation secondary actions

export const getAllocationSecondaryActionsConfiguration =
  (): Array<DataTableActionConfiguration> => {
    return [getAllocationBundleAction()]
  }

const getAllocationBundleAction = (): DataTableActionConfiguration => {
  return {
    icon: ActionIcons.BUNDLE
  }
}

export const filterAllocationApplicableSecondaryActionsConfiguration = (
  item: AllocationModel,
  configurations: Array<DataTableActionConfiguration>
): Array<DataTableActionConfiguration> => {
  let applicableActionsConfiguration: Array<DataTableActionConfiguration> = []
  configurations.forEach(configuration => {
    switch (configuration.icon) {
      case ActionIcons.BUNDLE: {
        if (item.bundledParentProduct !== null) {
          configuration.tooltip = i18n.t('allocations:dataTable.tooltips.bundled', {
            parentId: item.bundledParentProduct
          })
          configuration.noHandlerTooltip = i18n.t('allocations:dataTable.tooltips.bundled', {
            parentId: item.bundledParentProduct
          })
          applicableActionsConfiguration.push(configuration)
        }
        break
      }
    }
  })
  return applicableActionsConfiguration
}

//#endregion
